'use client'

import { useMemo, useState } from 'react'

function verdictFor(ratio: number) {
  if (ratio < 1)
    return {
      label: 'Unsustainable',
      note: 'You spend more to acquire a customer than they’re worth.',
      accent: 'border-danger/30 bg-danger/10',
    }
  if (ratio < 3)
    return {
      label: 'Thin',
      note: 'Workable, but margins are tight — efficiency gains will help before scaling hard.',
      accent: 'border-warn/30 bg-warn/10',
    }
  if (ratio <= 5)
    return {
      label: 'Healthy',
      note: 'A strong, scalable ratio — the classic target is around 3:1.',
      accent: 'border-success/30 bg-success/10',
    }
  return {
    label: 'Underinvesting?',
    note: 'Very efficient — you may be able to spend more to grow faster.',
    accent: 'border-success/30 bg-success/10',
  }
}

export default function LtvCacCalculator() {
  const [ltv, setLtv] = useState('')
  const [cac, setCac] = useState('')

  const result = useMemo(() => {
    const l = parseFloat(ltv.replace(/,/g, ''))
    const c = parseFloat(cac.replace(/,/g, ''))
    if (!isNaN(l) && !isNaN(c) && c > 0 && l >= 0) {
      const ratio = l / c
      return { ratio, verdict: verdictFor(ratio) }
    }
    return null
  }, [ltv, cac])

  function reset() {
    setLtv('')
    setCac('')
  }

  return (
    <div className="mt-6 card p-8">
      <div className="grid gap-6 sm:grid-cols-2">
        <label className="block">
          <span className="text-sm font-medium text-fg">Customer lifetime value (LTV)</span>
          <div className="mt-2 flex items-center rounded-md border border-line bg-bg px-3">
            <span className="text-muted">$</span>
            <input
              type="number"
              min={0}
              value={ltv}
              onChange={(e) => setLtv(e.target.value)}
              placeholder="e.g. 4500"
              className="w-full bg-transparent py-2 pl-2 text-fg outline-none"
            />
          </div>
          <span className="mt-1 block text-xs text-muted">
            Total gross profit you expect from one customer over their lifetime.
          </span>
        </label>

        <label className="block">
          <span className="text-sm font-medium text-fg">Customer acquisition cost (CAC)</span>
          <div className="mt-2 flex items-center rounded-md border border-line bg-bg px-3">
            <span className="text-muted">$</span>
            <input
              type="number"
              min={0}
              value={cac}
              onChange={(e) => setCac(e.target.value)}
              placeholder="e.g. 1250"
              className="w-full bg-transparent py-2 pl-2 text-fg outline-none"
            />
          </div>
          <span className="mt-1 block text-xs text-muted">
            What it costs you to acquire one customer.
          </span>
        </label>
      </div>

      <div className="mt-8 flex flex-col items-center rounded-md bg-accent/10 p-6 text-center">
        <span className="text-xs uppercase tracking-widest text-fg-secondary">LTV : CAC ratio</span>
        <span className="mt-2 text-4xl font-semibold text-fg">
          {result ? `${result.ratio.toFixed(2)} : 1` : '0.00 : 1'}
        </span>
      </div>

      {result && (
        <div className={`mt-4 rounded-md border p-5 text-center ${result.verdict.accent}`}>
          <span className="text-lg font-semibold text-fg">{result.verdict.label}</span>
          <p className="mt-1 text-sm text-muted">{result.verdict.note}</p>
        </div>
      )}

      <button
        type="button"
        onClick={reset}
        className="mt-6 text-sm text-accent underline-offset-4 hover:underline"
      >
        Clear values
      </button>
    </div>
  )
}
